import prettyBytes from 'pretty-bytes'
import { resourcePackState } from './resourcePackState'
import { setLoadingScreenStatus } from './appStatus'
import { showReplayError, classifyReplayError } from './react/replayErrorState'
import { appQueryParams } from './appParams'
import { createFullScreenProgressReporter } from './core/progressReporter'
import { ConnectOptions } from './connect'

export const getFixedFilesize = (bytes: number) => {
  return prettyBytes(bytes, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const getTexturepackUrlCandidates = (url: string) => {
  const candidates = [url]
  if (url.includes(' ')) {
    candidates.push(url.replaceAll(' ', '+'))
  }
  return [...new Set(candidates)]
}

const hasZipSignature = (buffer: ArrayBuffer) => {
  if (buffer.byteLength < 4) return false
  const signature = new Uint8Array(buffer, 0, 4)
  if (signature[0] !== 0x50 || signature[1] !== 0x4b) return false
  const third = signature[2]
  const fourth = signature[3]
  return (
    (third === 0x03 && fourth === 0x04)
    || (third === 0x05 && fourth === 0x06)
    || (third === 0x07 && fourth === 0x08)
  )
}

export const isInterestedInDownload = () => {
  const { texturepack, replayFileUrl } = appQueryParams
  return !!texturepack || !!replayFileUrl
}

const inner = async () => {
  const { texturepack, replayFileUrl } = appQueryParams
  return downloadAndOpenMapFromUrl(undefined, texturepack, undefined, replayFileUrl)
}

export const downloadAndOpenMapFromUrl = async (mapUrl: string | undefined, texturepackUrl: string | undefined, mapUrlDir: string[] | undefined, replayFileUrl: string | undefined, connectOptions?: Partial<ConnectOptions>) => {
  if (!replayFileUrl && (mapUrl || mapUrlDir?.length || appQueryParams.mapDirGuess || appQueryParams.mapDirBaseUrl)) {
    setLoadingScreenStatus('World/map loading flow is disabled in replay-focused mode')
    return false
  }

  let didSomething = false

  if (texturepackUrl) {
    const { getResourcePackNames, installResourcepackPack, updateTexturePackInstalledState } = await import('./resourcePack')
    await updateTexturePackInstalledState()
    if (resourcePackState.resourcePackInstalled) {
      const installedPackName = Object.keys(await getResourcePackNames())[0] ?? 'current pack'
      if (!confirm(`You are going to install a new resource pack, which will REPLACE the current one: ${installedPackName} Continue?`)) return didSomething
    }
    const name = texturepackUrl.slice(texturepackUrl.lastIndexOf('/') + 1).slice(-25)
    const downloadThing = 'texturepack'
    setLoadingScreenStatus(`Downloading ${downloadThing} ${name}...`)

    const texturepackCandidates = getTexturepackUrlCandidates(texturepackUrl)
    let response: Response | undefined
    let finalTexturepackUrl: string | undefined
    let lastError: unknown
    for (const candidate of texturepackCandidates) {
      try {
        const res = await fetch(candidate)
        if (!res.ok) {
          lastError = new Error(`Failed to download texturepack: ${candidate} (${res.status} ${res.statusText})`)
          continue
        }

        const contentType = res.headers?.get('Content-Type') ?? ''
        if (contentType.includes('text/html')) {
          lastError = new Error(`Texturepack URL returned HTML instead of a ZIP archive: ${candidate}`)
          continue
        }

        response = res
        finalTexturepackUrl = candidate
        break
      } catch (err) {
        lastError = err
      }
    }

    if (!response || !finalTexturepackUrl) {
      throw lastError ?? new Error('Failed to download texturepack')
    }

    const contentLengthStr = response.headers?.get('Content-Length') ?? ''
    const contentLength = contentLengthStr && +contentLengthStr
    setLoadingScreenStatus(`Downloading ${downloadThing} ${name}: have to download ${contentLength && getFixedFilesize(contentLength)}...`)

    let downloadedBytes = 0
    const buffer = await new Response(new ReadableStream({
      async start (controller) {
        if (!response.body) throw new Error('Server returned no response!')
        const reader = response.body.getReader()

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read()

          if (done) {
            controller.close()
            break
          }

          downloadedBytes += value.byteLength

          // Calculate download progress as a percentage
          const progress = contentLength ? (downloadedBytes / contentLength) * 100 : undefined
          setLoadingScreenStatus(`Download ${downloadThing} progress: ${progress === undefined ? '?' : Math.floor(progress)}% (${getFixedFilesize(downloadedBytes)} / ${contentLength && getFixedFilesize(contentLength)})`, false, true)

          // Pass the received data to the controller
          controller.enqueue(value)
        }
      },
    })).arrayBuffer()

    if (!hasZipSignature(buffer)) {
      throw new Error('Downloaded texturepack is not a ZIP archive')
    }

    const installedName = finalTexturepackUrl.slice(finalTexturepackUrl.lastIndexOf('/') + 1).slice(-30)
    await installResourcepackPack(buffer, createFullScreenProgressReporter(), installedName)
    didSomething = true
  }

  if (replayFileUrl) {
    setLoadingScreenStatus('Downloading replay file')
    const candidates = (() => {
      const urls = [replayFileUrl]
      if (replayFileUrl.startsWith('assets/')) urls.push(replayFileUrl.slice('assets/'.length))
      if (replayFileUrl.startsWith('/assets/')) urls.push(replayFileUrl.slice('/assets/'.length))

      const withLeadingSlash = urls
        .filter(u => !(u.startsWith('http://') || u.startsWith('https://') || u.startsWith('/')))
        .map(u => `/${u}`)

      return [...new Set([...urls, ...withLeadingSlash])]
    })()

    let response: Response | undefined
    let finalReplayFileUrl: string | undefined
    let lastError: any
    for (const candidate of candidates) {
      try {
        const res = await fetch(candidate)
        if (res.ok) {
          response = res
          finalReplayFileUrl = candidate
          break
        }
        lastError = new Error(`Failed to download replay file: ${candidate} (${res.status} ${res.statusText})`)
      } catch (err) {
        lastError = err
      }
    }
    if (!response || !finalReplayFileUrl) {
      throw lastError ?? new Error('Failed to download replay file')
    }
    const contentLength = response.headers?.get('Content-Length')
    const size = contentLength ? +contentLength : undefined
    const filename = finalReplayFileUrl.split('/').pop()

    let downloadedBytes = 0
    const buffer = await new Response(new ReadableStream({
      async start (controller) {
        if (!response.body) throw new Error('Server returned no response!')
        const reader = response.body.getReader()

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read()

          if (done) {
            controller.close()
            break
          }

          downloadedBytes += value.byteLength

          // Calculate download progress as a percentage
          const progress = size ? (downloadedBytes / size) * 100 : undefined
          setLoadingScreenStatus(`Download replay file progress: ${progress === undefined ? '?' : Math.floor(progress)}% (${getFixedFilesize(downloadedBytes)} / ${size && getFixedFilesize(size)})`, false, true)

          // Pass the received data to the controller
          controller.enqueue(value)
        }
      },
    })).arrayBuffer()

    // Convert buffer to text, handling any compression automatically
    const decoder = new TextDecoder()
    const contents = decoder.decode(buffer)

    const { openFile } = await import('./packetsReplay/replayPackets')
    openFile({
      contents,
      filename,
      filesize: size
    })
    didSomething = true
  }

  return didSomething
}

export default async () => {
  try {
    return await inner()
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err)
    setLoadingScreenStatus(`Failed to download/open replay or texturepack. Reason: ${message}`, true, true)
    // This funnel is the download/open path → default unmatched failures to the
    // network window (the actionable "retry" case).
    const match = classifyReplayError(err)
    showReplayError(match?.id ?? 'network', { vars: match?.vars })
    return true
  }
}
